import type { LocalizedDict } from './index'

export const adminUsersWorkspaceI18n = {
  en: {
    eyebrow: 'Access & users · 01',
    title: 'Team & access',
    subtitle: 'Manage internal platform memberships and explicit capability revocations. External partners stay in their governed onboarding flows.',
    invite: 'Invite team member',
    team: 'My team',
    pending: 'Pending invitations',
    directory: 'Platform directory',
    internalMembers: 'Internal members',
    activeMemberships: 'Active memberships',
    accessModel: 'Access model',
    roleBased: 'Role-based',
    search: 'Search name or email',
    allRoles: 'All roles',
    allStatuses: 'All statuses',
    member: 'Member',
    role: 'Role',
    effectiveAccess: 'Effective access',
    joined: 'Joined',
    status: 'Status',
    platformOwner: 'Platform owner',
    internalStaff: 'General staff',
    roleOps: 'Operations',
    roleFinance: 'Finance',
    roleReadonly: 'Read-only',
    roleInternalStaffHint:
      'Everything except bank accounts and transfers — catalog, orders, finance, team management, platform settings.',
    roleOpsHint:
      'Catalog, orders, shipping, inventory and production in full; pricing and finance read-only; no bank, no team management.',
    roleFinanceHint:
      'Pricing, settlements, payouts and reports in full; bank accounts view-only (no transfers); operations read-only.',
    roleReadonlyHint:
      'Views operational and financial screens only. No editing, no bank, no credentials, no team management.',
    profile: 'Profile',
    audit: 'Audit',
    persona: 'Persona',
    lastLogin: 'Last login',
    notAvailable: 'Not available',
    reason: 'Change reason',
    reasonPlaceholder: 'Required for governed changes',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    remove: 'Remove from team',
    cancel: 'Cancel',
    confirm: 'Confirm',
    removalCopy: 'This removes only this organization membership. The identity is not deleted.',
    lifecycleCopy: 'This changes this membership lifecycle state and signs the member out.',
    rolePreset: 'Role preset',
    saveRole: 'Save role',
    capabilityHelp: 'The role grants the base set. Switches only revoke or restore governed access.',
    saveAccess: 'Save access',
    auditLoading: 'Loading audit timeline…',
    auditLoadError: 'Audit timeline could not be loaded.',
    auditEmpty: 'No governed changes recorded for this member yet.',
    auditRoleChanged: 'Role / status updated',
    auditAccessReplaced: 'Capability access changed',
    auditRemoved: 'Removed from team',
    auditJoined: 'Joined the team',
    auditReactivated: 'Membership reactivated',
    auditSessionsRevoked: 'Sessions signed out',
    selectMember: 'Select a team member to inspect profile and access.',
    loadingTeam: 'Loading team…',
    loadError: 'Team could not be loaded',
    noMatch: 'No team members match these filters',
    noTerminated: 'No terminated members',
    permissions: 'Permissions',
    roles: 'Roles',
    loadingRoles: 'Loading role presets…',
    rolesListTitle: 'Role presets',
    rolesListHelp: 'What each role grants by default. Editing a preset changes the base access of everyone holding it.',
    rolesMembers: 'members',
    rolesOwnerLocked: 'Locked',
    rolesOwnerLockedHelp: 'The Platform owner preset is a structural invariant and cannot be edited.',
    rolesEditorHelp: 'Ticked = the role grants it. Per-person removals from the Permissions tab stay layered on top.',
    rolesConfirmTitle: 'Apply this preset change?',
    rolesConfirmBody: 'You changed {changes} permissions on {role}. The new base set applies immediately to {count} active members.',
    rolesConfirmSave: 'Apply to role',
    rolesReview: 'Review & save',
    saving: 'Saving…',
    permissionsOn: 'on',
    searchPeople: 'Search people',
    searchPermissions: 'Search permissions',
    onlyTurnedOff: 'Only turned off',
    expandAll: 'Expand all',
    collapseAll: 'Collapse all',
    unsavedChanges: 'Unsaved changes',
    discard: 'Discard',
    changed: 'changed',
    noPermissionsMatch: 'No permissions match this search',
    nothingTurnedOff: 'Nothing is turned off — this person has every permission of the role',
    unsavedSwitch: 'Save or discard the changes before switching to another person.',
    roleChangeNotice: 'Save the role first. The permission list is rebuilt for the new role once it is saved.',
    roleChangePending: 'Role change',
    protectedSelf: 'You cannot change your own access.',
    protectedLastOwner: 'The last active platform owner cannot be restricted.',
    protectedNoAuthority: 'You do not have the authority to change this person\'s access.',
    pendingHelp: 'Invitations scoped by the server to the current platform organization.',
    noInvites: 'No pending invitations',
    directoryHelp: 'Identities and organization memberships. For seller accounts you can hide prices per person.',
    prices: 'Prices',
    pricesVisible: 'Visible',
    pricesHidden: 'Hidden',
    pricesUpdating: 'Updating…',
    pricesNotApplicable: '—',
    showPricesTo: 'Show prices to {name}',
    hidePricesFrom: 'Hide prices from {name}',
    pricesError: "Couldn't change price visibility. Try again.",
    owners: 'owners',
    staff: 'staff',
    suspendedInactive: 'suspended or inactive',
    revocationsVisible: 'Explicit revocations are visible',
    active: 'Active',
    suspended: 'Suspended',
    terminated: 'Terminated',
    effective: 'effective',
    loadingInvites: 'Loading invitations…',
    inviteLoadError: 'Pending invitations could not be loaded.',
    expires: 'expires',
    resend: 'Resend',
    revoke: 'Revoke',
    confirmRevoke: 'Confirm revoke',
    searchIdentities: 'Search identities',
    loadingDirectory: 'Loading directory…',
    directoryLoadError: 'Platform directory could not be loaded.',
    noDirectory: 'No directory memberships match',
    identity: 'Identity',
    organization: 'Organization',
    type: 'Type',
    tier: 'Tier',
    noEmail: 'No email',
    inviteTitle: 'Invite a platform team member',
    inviteHelp: 'The invitation is scoped server-side to your current platform organization.',
    email: 'Email',
    expiresIn: 'Expires in',
    days: 'days',
    personalNote: 'Personal note (optional)',
    sendInvitation: 'Send invitation',
    teamViews: 'Team views',
  },
  tr: {
    eyebrow: 'Erişim ve kullanıcılar · 01',
    title: 'Ekip ve erişim',
    subtitle: 'Dahili platform üyeliklerini ve açık yetki iptallerini yönetin. Harici iş ortakları kendi yönetilen katılım akışlarında kalır.',
    invite: 'Ekip üyesi davet et',
    team: 'Ekibim',
    pending: 'Bekleyen davetler',
    directory: 'Platform dizini',
    internalMembers: 'Dahili üyeler',
    activeMemberships: 'Aktif üyelikler',
    accessModel: 'Erişim modeli',
    roleBased: 'Rol tabanlı',
    search: 'Ad veya e-posta ara',
    allRoles: 'Tüm roller',
    allStatuses: 'Tüm durumlar',
    member: 'Üye',
    role: 'Rol',
    effectiveAccess: 'Etkin erişim',
    joined: 'Katılım',
    status: 'Durum',
    platformOwner: 'Platform sahibi',
    internalStaff: 'Genel personel',
    roleOps: 'Operasyon',
    roleFinance: 'Finans',
    roleReadonly: 'Salt-okunur',
    roleInternalStaffHint:
      'Banka hesapları ve transferler hariç her şey — katalog, siparişler, finans, ekip yönetimi, platform ayarları.',
    roleOpsHint:
      'Katalog, sipariş, kargo, envanter ve üretim tam; fiyatlandırma ve finans salt-okunur; banka ve ekip yönetimi yok.',
    roleFinanceHint:
      'Fiyatlandırma, mutabakat, ödemeler ve raporlar tam; banka hesapları salt-görüntüleme (transfer yok); operasyon salt-okunur.',
    roleReadonlyHint:
      'Operasyon ve finans ekranlarını yalnız görüntüler. Düzenleme, banka, credential ve ekip yönetimi yok.',
    profile: 'Profil',
    audit: 'Denetim',
    persona: 'Persona',
    lastLogin: 'Son giriş',
    notAvailable: 'Mevcut değil',
    reason: 'Değişiklik nedeni',
    reasonPlaceholder: 'Yönetilen değişiklikler için zorunlu',
    suspend: 'Askıya al',
    reactivate: 'Yeniden etkinleştir',
    remove: 'Ekipten kaldır',
    cancel: 'İptal',
    confirm: 'Onayla',
    removalCopy: 'Bu işlem yalnızca bu organizasyon üyeliğini kaldırır. Kimlik silinmez.',
    lifecycleCopy: 'Bu işlem üyeliğin yaşam döngüsü durumunu değiştirir ve kullanıcının oturumunu kapatır.',
    rolePreset: 'Rol ön ayarı',
    saveRole: 'Rolü kaydet',
    capabilityHelp: 'Rol temel yetki kümesini verir. Anahtarlar yalnızca yönetilen erişimi geri çeker veya geri yükler.',
    saveAccess: 'Erişimi kaydet',
    auditLoading: 'Denetim çizelgesi yükleniyor…',
    auditLoadError: 'Denetim çizelgesi yüklenemedi.',
    auditEmpty: 'Bu üye için kayıtlı yönetilen değişiklik yok.',
    auditRoleChanged: 'Rol / durum güncellendi',
    auditAccessReplaced: 'Yetki erişimi değişti',
    auditRemoved: 'Ekipten çıkarıldı',
    auditJoined: 'Ekibe katıldı',
    auditReactivated: 'Üyelik yeniden etkin',
    auditSessionsRevoked: 'Sonlandırılan oturum',
    selectMember: 'Profil ve erişimi incelemek için bir ekip üyesi seçin.',
    loadingTeam: 'Ekip yükleniyor…',
    loadError: 'Ekip yüklenemedi',
    noMatch: 'Bu filtrelerle eşleşen ekip üyesi yok',
    noTerminated: 'Sonlandırılmış üye yok',
    permissions: 'Yetkiler',
    roles: 'Roller',
    loadingRoles: 'Rol ön ayarları yükleniyor…',
    rolesListTitle: 'Rol ön ayarları',
    rolesListHelp: 'Her rolün varsayılan olarak ne verdiği. Ön ayarı düzenlemek o roldeki herkesin taban erişimini değiştirir.',
    rolesMembers: 'üye',
    rolesOwnerLocked: 'Kilitli',
    rolesOwnerLockedHelp: 'Platform sahibi ön ayarı yapısal bir kuraldır ve düzenlenemez.',
    rolesEditorHelp: 'İşaretli = rol bunu verir. Yetkiler sekmesindeki kişi bazlı kısıtlamalar bunun üzerinde kalır.',
    rolesConfirmTitle: 'Bu ön ayar değişikliği uygulansın mı?',
    rolesConfirmBody: '{role} rolünde {changes} yetki değiştirdin. Yeni taban küme {count} aktif üyeye anında uygulanır.',
    rolesConfirmSave: 'Role uygula',
    rolesReview: 'İncele ve kaydet',
    saving: 'Kaydediliyor…',
    permissionsOn: 'açık',
    searchPeople: 'Kişi ara',
    searchPermissions: 'Yetki ara',
    onlyTurnedOff: 'Sadece kapalı olanlar',
    expandAll: 'Tümünü aç',
    collapseAll: 'Tümünü kapat',
    unsavedChanges: 'Kaydedilmemiş değişiklik',
    discard: 'Vazgeç',
    changed: 'değişti',
    noPermissionsMatch: 'Bu aramayla eşleşen yetki yok',
    nothingTurnedOff: 'Kapalı yetki yok — bu kişi rolün tüm yetkilerine sahip',
    unsavedSwitch: 'Başka bir kişiye geçmeden önce değişiklikleri kaydedin ya da vazgeçin.',
    roleChangeNotice: 'Önce rolü kaydedin. Rol kaydedilince yetki listesi yeni role göre yenilenir.',
    roleChangePending: 'Rol değişikliği',
    protectedSelf: 'Kendi erişiminizi değiştiremezsiniz.',
    protectedLastOwner: 'Son aktif platform sahibinin yetkileri kısıtlanamaz.',
    protectedNoAuthority: 'Bu kişinin erişimini değiştirme yetkiniz yok.',
    pendingHelp: 'Davetler sunucu tarafından mevcut platform organizasyonuna göre kapsamlanır.',
    noInvites: 'Bekleyen davet yok',
    directoryHelp: 'Kimlik ve organizasyon üyelikleri. Satıcı hesaplarında fiyatları kişi kişi gizleyebilirsiniz.',
    prices: 'Fiyatlar',
    pricesVisible: 'Görünür',
    pricesHidden: 'Gizli',
    pricesUpdating: 'Güncelleniyor…',
    pricesNotApplicable: '—',
    showPricesTo: '{name} fiyatları görsün',
    hidePricesFrom: '{name} fiyatları görmesin',
    pricesError: 'Fiyat görünürlüğü değiştirilemedi. Tekrar deneyin.',
    owners: 'sahip',
    staff: 'ekip üyesi',
    suspendedInactive: 'askıda veya pasif',
    revocationsVisible: 'Açık yetki iptalleri görünür',
    active: 'Aktif',
    suspended: 'Askıda',
    terminated: 'Sonlandırıldı',
    effective: 'etkin',
    loadingInvites: 'Davetler yükleniyor…',
    inviteLoadError: 'Bekleyen davetler yüklenemedi.',
    expires: 'sona erme',
    resend: 'Yeniden gönder',
    revoke: 'İptal et',
    confirmRevoke: 'İptali onayla',
    searchIdentities: 'Kimliklerde ara',
    loadingDirectory: 'Dizin yükleniyor…',
    directoryLoadError: 'Platform dizini yüklenemedi.',
    noDirectory: 'Eşleşen dizin üyeliği yok',
    identity: 'Kimlik',
    organization: 'Organizasyon',
    type: 'Tür',
    tier: 'Seviye',
    noEmail: 'E-posta yok',
    inviteTitle: 'Platform ekip üyesi davet et',
    inviteHelp: 'Davet sunucu tarafından mevcut platform organizasyonunuza göre kapsamlanır.',
    email: 'E-posta',
    expiresIn: 'Geçerlilik',
    days: 'gün',
    personalNote: 'Kişisel not (isteğe bağlı)',
    sendInvitation: 'Daveti gönder',
    teamViews: 'Ekip görünümleri',
  },
} satisfies LocalizedDict<Record<string, string>>
